import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { z as z4 } from 'zod/v4';

import { useForm, useFormContext } from 'react-hook-form';

import { useShippingMethodListSuspenseQuery } from '~src/main/queries';

const address = z4.object({
  address_lines: z4.array(z4.string()).optional(),
  apartment_number: z4.string().optional(),
  attn: z4.string().optional(),
  building_name: z4.string().optional(),
  care_of: z4.string().optional(),
  city: z4.string().optional(),
  coordinates: z4
    .object({
      lat: z4.number().optional(),
      lng: z4.number().optional(),
    })
    .optional(),
  country: z4.string().optional(),
  door_code: z4.string().optional(),
  floor_number: z4.string().optional(),
  name: z4.string().optional(),
  po_box_id: z4.string().optional(),
  postal_code: z4.string().optional(),
  region: z4.string().optional(),
  street: z4.string().optional(),
  street_number: z4.string().optional(),
  subregion: z4.string().optional(),
});

export const bookingRuleInputSchema = z.object({
  json: z.string(),
});

export const chatSchema = z.object({
  prompt: z.string().min(1),
});

export function useBookingRuleOutputSchema() {
  const { data: methodsInclCustom } = useShippingMethodListSuspenseQuery({
    customBookingMethods: false,
    onlyConfigured: true,
  });

  const { data: methodsExclCustom } = useShippingMethodListSuspenseQuery({
    customBookingMethods: true,
    onlyConfigured: true,
  });

  const stepExpression = z4.object({
    expression: z4.string(),
  });

  const step = z4.object({
    filters: z4.array(stepExpression).optional(),
    actions: z4.array(stepExpression),
    name: z4.string(),
  });

  const returnBooking = z4.object({
    use_address_from_as_return: z4.boolean().optional(),
    label_consolidation: z4.boolean().optional(),
    method: z4.string(),
  });

  const rule = z4.object({
    booking_method: z4.union(methodsExclCustom.map(method => z4.literal(method.id!))).optional(),
    method: z4.union([...methodsInclCustom.map(method => z4.literal(method.id!)), z4.string()]),
    addresses: z4.object({ return: address.optional() }).optional(),
    return_booking: returnBooking.optional(),
    steps: z4.array(step).optional(),
    carrier: z4.string().optional(),
    name: z4.string(),
  });

  return z4.array(rule);
}

export function useBookingRuleForm({ defaultValue }: { defaultValue: BookingRuleInputSchema['json'] }) {
  return useForm({
    resolver: zodResolver(bookingRuleInputSchema),
    defaultValues: {
      json: defaultValue,
    },
  });
}

export function useChatForm() {
  return useForm<z.infer<typeof chatSchema>>({
    resolver: zodResolver(chatSchema),
    defaultValues: {
      prompt: '',
    },
  });
}

export function useShipmentSchema() {
  const addon = z4.object({
    data: z4.record(z4.string(), z4.string()).optional(),
    description: z4.string().optional(),
    code: z4.string().optional(),
    name: z4.string().optional(),
  });

  const customer = z4.object({
    address: address.optional(),
    email: z4.string().optional(),
    phone: z4.string().optional(),
    national_identification_number: z4.string().optional(),
  });

  const dimensions = z4.object({
    height: z4.coerce.number().optional(),
    length: z4.coerce.number().optional(),
    width: z4.coerce.number().optional(),
  });

  const lineItem = z4.object({
    currency: z4.string().optional(),
    description: z4.string().optional(),
    dimensions: dimensions.optional(),
    hs_tariff_number: z4.string().optional(),
    name: z4.string().optional(),
    price: z4.coerce.number().optional(),
    quantity: z4.coerce.number().optional(),
    sku: z4.string().optional(),
    tags: z4.array(z4.string()).optional(),
    weight: z4.coerce.number().optional(),
  });

  const contents = z4.object({
    description: z4.string().optional(),
    external_orders: z4.array(z4.object({ external_id: z4.string().optional() })).optional(),
    goods: z4.array(lineItem).optional(),
    transport_orders: z4
      .array(
        z4.object({
          external_ref: z4.string().optional(),
          tos_id: z4.string().optional(),
        })
      )
      .optional(),
  });

  const range = z4.object({
    start: z4.string().optional(),
    end: z4.string().optional(),
  });

  const parcel = z4.object({
    contents: contents.optional(),
    dimensions: dimensions.optional(),
    free_text: z4.string().optional(),
    label_free_text: z4.string().optional(),
    weight: z4.coerce.number().optional(),
  });

  const identificationNumbers = z4.object({
    ein: z4.string().optional(),
    eori: z4.string().optional(),
    hmrc: z4.string().optional(),
    ioss: z4.string().optional(),
    pan: z4.string().optional(),
    pccc: z4.string().optional(),
    ssn: z4.string().optional(),
    tin: z4.string().optional(),
    ukims: z4.string().optional(),
    vat: z4.string().optional(),
    voec: z4.string().optional(),
  });

  const contact = z4.object({
    email: z4.string().optional(),
    phone: z4.string().optional(),
  });

  const declarationItem = z4.object({
    additional_information: z4.array(z4.string()).optional(),
    brand: z4.string().optional(),
    category: z4.string().optional(),
    country_of_origin: z4.string().optional(),
    description: z4.string().optional(),
    hs_tariff_number: z4.string().optional(),
    name: z4.string().optional(),
    quantity: z4.coerce.number().optional(),
    references: z4
      .array(
        z4.object({
          code: z4.string().optional(),
          type: z4.string().optional(),
        })
      )
      .optional(),
    sku: z4.string().optional(),
    unit_alternative_value: z4.coerce.number().optional(),
    unit_gross_weight: z4.coerce.number().optional(),
    unit_net_weight: z4.coerce.number().optional(),
    unit_value: z4.coerce.number().optional(),
  });

  const incoterms = z4.enum([
    'INCOTERMS_UNKNOWN',
    'EXW',
    'FCA',
    'FAS',
    'FOB',
    'CFR',
    'CIF',
    'CPT',
    'CIP',
    'DAT',
    'DAP',
    'DDP',
    'DAF',
    'DDU',
    'DEQ',
    'DES',
    'DPU',
  ]);

  const contentsType = z4.enum([
    'UNKNOWN',
    'DOCUMENTS',
    'GIFT',
    'COMMERCIAL_SAMPLE',
    'SALE',
    'RETURN',
    'OTHER',
    'DANGEROUS_GOODS',
    'HUMANITARIAN_DONATIONS',
  ]);

  const customs = z4.object({
    general_customs_declaration: z4
      .object({
        buyer_contact: contact.optional(),
        buyer_identification_numbers: identificationNumbers.optional(),
        buyer_tax_id_number: z4.string().optional(),
        contents_explanation: z4.string().optional(),
        contents_type: contentsType.optional(),
        currency: z4.string().optional(),
        declaration_statement: z4.string().optional(),
        incoterms: incoterms.optional(),
        invoice_date: z4.string().optional(),
        invoice_number: z4.string().optional(),
        items: z4.array(declarationItem).optional(),
        place_of_incoterms: z4.string().optional(),
        seller_address: address.optional(),
        seller_contact: contact.optional(),
        seller_identification_numbers: identificationNumbers.optional(),
        seller_tax_id_number: z4.string().optional(),
        sold_to: address.optional(),
        special_instructions: z4.string().optional(),
        total_gross_weight: z4.coerce.number().optional(),
      })
      .optional(),
  });

  return z4.object({
    addons: z4.array(addon).optional(),
    address_from: address,
    address_from_location_ref: z4.string().optional(),
    address_return: address.optional(),
    address_to: address,
    carrier_contract_id: z4.string().optional(),
    contents: contents.optional(),
    courier_instructions: z4.string().optional(),
    customs_declaration: customs.optional(),
    customer_info: customer,
    delivery_time: range.optional(),
    dimensions: dimensions.optional(),
    direction_type: z4.enum(['OUTBOUND', 'RETURN', 'UNSPECIFIED']).optional(),
    external_id: z4.string().optional(),
    line_items: z4.array(lineItem).optional(),
    location_ref: z4.string().optional(),
    location_type: z4.enum(['UNKNOWN_PICKUP_LOCATION_TYPE', 'LOCKER', 'STORE', 'POSTOFFICE', 'MANNED']).optional(),
    meta: z4.record(z4.string(), z4.string()).optional(),
    number_of_parcels: z4.number().optional(),
    outbound_shipment_id: z4.string().optional(),
    parcel_free_text: z4.string().optional(),
    parcels: z4.array(parcel).optional(),
    shipment_value: z4.coerce.number().optional(),
    shipping_category_ref: z4.string().optional(),
    shipping_date: z4.string().optional(),
    shipping_method: z4.string().optional(),
    tos_id: z4.string().optional(),
    weight: z4.coerce.number().optional(),
  });
}

export const useBookingRuleFormContext = useFormContext<BookingRuleInputSchema>;

export type BookingRuleOutputSchema = z4.infer<ReturnType<typeof useBookingRuleOutputSchema>>;
export type BookingRuleInputSchema = z.infer<typeof bookingRuleInputSchema>;
